import { useMemo } from "react";
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { BaseChartProps, ChartWrapper } from "./chart-utils";

import {
  chartColors,
  formatNumber,
  defaultResponsiveConfig,
  generateRandomData,
} from "./chart-helpers";

import {
  VerticalAlignmentType,
  HorizontalAlignmentType,
} from "recharts/types/component/DefaultLegendContent";

interface LineChartProps extends BaseChartProps {
  strokeWidth?: number;
  curved?: boolean;
  fillOpacity?: number;
  showDots?: boolean;
  areaFill?: boolean;
}

export default function LineChart({
  data,
  series,
  title,
  subtitle,
  height = 300,
  width = "100%",
  className,
  colors = chartColors.series,
  showLegend = true,
  showTooltip = true,
  showGrid = true,
  animate = true,
  legendPosition = "bottom",
  strokeWidth = 2,
  curved = true,
  showDots = true,
  areaFill = false,
  fillOpacity = 0.3,
}: LineChartProps) {
  const chartData = useMemo(() => {
    if (series && series.length > 0) {
      const categories = [
        ...new Set(series.flatMap((s) => s.data.map((d) => d.name))),
      ];
      return categories.map((category) => {
        const item: Record<string, string | number> = { name: category };
        series.forEach((s) => {
          const dataPoint = s.data.find((d) => d.name === category);
          item[s.name] = dataPoint ? dataPoint.value : 0;
        });
        return item;
      });
    }

    return data || generateRandomData(8, 100);
  }, [data, series]);

  const legendProps = useMemo(() => {
    const baseProps = {
      wrapperStyle: {
        paddingTop: legendPosition === "bottom" ? 20 : 0,
      },
    };

    switch (legendPosition) {
      case "top":
        return { ...baseProps, verticalAlign: "top" as VerticalAlignmentType };
      case "right":
        return {
          ...baseProps,
          verticalAlign: "middle" as VerticalAlignmentType,
          align: "right" as HorizontalAlignmentType,
        };
      case "left":
        return {
          ...baseProps,
          verticalAlign: "middle" as VerticalAlignmentType,
          align: "left" as HorizontalAlignmentType,
        };
      default:
        return {
          ...baseProps,
          verticalAlign: "bottom" as VerticalAlignmentType,
        };
    }
  }, [legendPosition]);

  return (
    <ChartWrapper
      title={title}
      subtitle={subtitle}
      className={className}
      height={height}
      width={width}
    >
      <ResponsiveContainer {...defaultResponsiveConfig}>
        <RechartsLineChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
        >
          {showGrid && (
            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
          )}
          <XAxis
            dataKey="name"
            stroke={chartColors.foreground}
            tick={{ fill: chartColors.foreground }}
          />
          <YAxis
            tickFormatter={formatNumber}
            stroke={chartColors.foreground}
            tick={{ fill: chartColors.foreground }}
          />
          {showTooltip && <Tooltip />}
          {showLegend && <Legend {...legendProps} />}

          {series && series.length > 0 ? (
            series.map((s, idx) => (
              <Line
                key={s.name}
                type={curved ? "monotone" : "linear"}
                dataKey={s.name}
                stroke={colors[idx % colors.length]}
                strokeWidth={strokeWidth}
                dot={showDots}
                activeDot={{ r: 8 }}
                isAnimationActive={animate}
                fill={areaFill ? colors[idx % colors.length] : undefined}
                fillOpacity={areaFill ? fillOpacity : 0}
              />
            ))
          ) : (
            <Line
              type={curved ? "monotone" : "linear"}
              dataKey="value"
              stroke={colors[0]}
              strokeWidth={strokeWidth}
              dot={showDots}
              activeDot={{ r: 8 }}
              isAnimationActive={animate}
              fill={areaFill ? colors[0] : undefined}
              fillOpacity={areaFill ? fillOpacity : 0}
            />
          )}
        </RechartsLineChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
}
